/* eslint-disable max-lines-per-function */
import { startTransition, useEffect, useMemo, useRef, useState, type VideoHTMLAttributes } from 'react';
import { VideoPlayer } from './VideoStream';
import { useHasInteractedIfNeeded } from './useHasInteractedIfNeeded';

type UseVideoStreamProps = {
    streamId: string;
    muted: boolean;
    loop: boolean;
    autoPlay: boolean;
    captions?: boolean;
    controls?: boolean;
    load: boolean;
} & Omit<VideoHTMLAttributes<HTMLVideoElement>, 'preload'>;

export const useVideoStream = ({
    streamId,
    muted,
    loop,
    autoPlay,
    load,
    captions = false,
    controls = false,
    ...rest
}: UseVideoStreamProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const playerRef = useRef<VideoPlayer | null>(null);
    const [hasLoaded, setHasLoaded] = useState(false);
    const hasInteractedIfNeeded = useHasInteractedIfNeeded();

    // Controls are only initially shown if explicitly set and autoPlay is not enabled
    // If autoPlay is enabled, controls are shown after our pseudo autoplay functionality below
    const [showControls, setShowControls] = useState(() => (controls ? !autoPlay : false));

    useEffect(() => {
        let frameId: number;
        if (!load || !hasInteractedIfNeeded) return undefined;

        const initialize = () => {
            if (videoRef.current) {
                const player = new VideoPlayer(videoRef.current, streamId);
                playerRef.current = player;

                player.init(() => {
                    startTransition(() => {
                        setHasLoaded(true);
                    });
                });
            } else {
                frameId = requestAnimationFrame(initialize);
            }
        };

        initialize();

        return () => {
            cancelAnimationFrame(frameId);
        };
    }, [hasInteractedIfNeeded, load, streamId]);

    useEffect(() => {
        if (hasLoaded && !load) {
            playerRef.current?.destroy();
            playerRef.current = null;
            setHasLoaded(false);
        }
    }, [load, hasLoaded]);

    useEffect(() => {
        if (!playerRef.current) return;
        playerRef.current.mute(muted);
    }, [muted]);

    useEffect(() => {
        if (!videoRef.current || !playerRef.current || !hasLoaded || !loop) return;

        const handleTimeUpdate = () => {
            const currentTime = playerRef.current?.getCurrentTime() ?? 0;
            const duration = playerRef.current?.getDuration() ?? 0;

            if (duration - currentTime < 0.2) {
                playerRef.current?.seek(0);
                playerRef.current?.play();
            }
        };

        videoRef.current.ontimeupdate = handleTimeUpdate;
        videoRef.current.onended = () => {
            // fallback for if the timeupdate event handler doesn't work
            playerRef.current?.seek(0);
            playerRef.current?.play();
        };
    }, [hasLoaded, loop]);

    useEffect(() => {
        if (!hasLoaded || !autoPlay || !playerRef.current) return;
        // This is a pseudo autoplay functionality that seems to perform more consistently
        // across different devices.
        playerRef.current.mute(true);
        playerRef.current.play();
        playerRef.current.mute(muted);
        playerRef.current.enableCaptions(captions);
        setShowControls(controls);
    }, [hasLoaded, autoPlay, captions, controls, muted]);

    const videoProps = useMemo(
        () => ({
            ref: videoRef,
            controls: showControls,
            loop,
            muted,
            ...rest,
        }),
        [showControls, loop, muted, rest],
    );

    return [playerRef, videoProps, hasLoaded] as const;
};
